import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import OnHumanTechLogo from "../tech/logos/OnHumanTechLogo";
import OnHumanHRLogo from "./logos/OnHumanHRLogo";

const EASE = [0.22, 1, 0.36, 1];

// Each brand's veil is a full-screen panel painted in the destination's own
// ground color, carrying the destination wordmark. The sweep direction mirrors
// the BrandSwitch geometry: HR sits on the left, Tech on the right, so moving
// to Tech travels rightward (panel enters from the left) and coming back to HR
// travels leftward (panel enters from the right).
const TARGETS = {
  tech: {
    path: "/onhumantech",
    bg: "#000000",
    from: "-100%",
    Logo: OnHumanTechLogo,
  },
  hr: {
    path: "/anamenu",
    bg: "#F7F8FA",
    from: "100%",
    Logo: OnHumanHRLogo,
  },
};

const BrandTransitionContext = createContext({
  startTechTransition: () => {},
  startHRTransition: () => {},
});

export function useBrandTransition() {
  return useContext(BrandTransitionContext);
}

// Sweeps a veil in the destination brand's ground color across the screen,
// then navigates. Mounted inside the Router but around the Routes so it
// survives the route change and can hold over the newly mounted page.
export default function BrandTransitionProvider({ children }) {
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [target, setTarget] = useState(null);
  const navigatedRef = useRef(false);

  const start = useCallback(
    (key) => {
      const next = TARGETS[key];
      if (!next) return;
      if (reduced) {
        navigate(next.path);
        return;
      }
      navigatedRef.current = false;
      setTarget(key);
    },
    [navigate, reduced]
  );

  const startTechTransition = useCallback(() => start("tech"), [start]);
  const startHRTransition = useCallback(() => start("hr"), [start]);

  const active = target ? TARGETS[target] : null;

  const handleSweepComplete = () => {
    if (navigatedRef.current || !active) return;
    navigatedRef.current = true;
    navigate(active.path);
    // Hold briefly over the newly mounted page, then release the veil.
    setTimeout(() => setTarget(null), 350);
  };

  return (
    <BrandTransitionContext.Provider value={{ startTechTransition, startHRTransition }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              key={`brand-veil-${target}`}
              className="fixed inset-0 z-[120] flex items-center justify-center"
              style={{ backgroundColor: active.bg }}
              initial={{ x: active.from }}
              animate={{ x: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.3, ease: EASE } }}
              transition={{ duration: 0.55, ease: EASE }}
              onAnimationComplete={handleSweepComplete}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.25, ease: EASE }}
              >
                {/* Scales down on narrow screens so the wider .tech wordmark
                    never runs past the viewport edge. */}
                <active.Logo className="h-14 sm:h-16 lg:h-20 w-auto max-w-[75vw]" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </BrandTransitionContext.Provider>
  );
}
